// The app shell: toolbar, save status, banners, notices, and the panels over the viewer.

import { ChevronLeft, ChevronRight, CloudCheck, CloudOff, CloudUpload, Ellipsis, Eye, House, ListOrdered, Lock, Minus, MousePointer2, Pencil, Plus, Redo2, SquareDashedMousePointer, TriangleAlert, Undo2, Users } from "lucide-preact";
import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { redo, redoStack, undo, undoStack } from "../edits.ts";
import { arranging, finishArrange, startArrange } from "../arrange.ts";
import { canEdit, cueListOpen, cueListView, dialog, dismiss, environment, ix, notices, panel, pending, presence, proposals, show, status, tabId, view } from "../state.ts";
import { downloadPending } from "../sync.ts";
import { currentPage, fit, goToPage, pageCount, scrollViewer, zoom, zoomBy } from "../viewer.ts";
import { scriptLoad } from "../loading.ts";
import { offline, usingOffline } from "../offline.ts";
import { CueList } from "./CueList.tsx";
import { Dialogs } from "./Dialogs.tsx";
import { Panel } from "./Panel.tsx";
import { Arrange } from "./Arrange.tsx";
import { CueNavigation } from "./CueNavigation.tsx";
import { ScriptLoading } from "./ScriptLoading.tsx";
import { ButtonTooltips } from "./ButtonTooltips.tsx";

const LONG_PENDING_MS = 10 * 60 * 1000;
const PDF_SCROLL_KEYS: Record<string, true> = { ArrowUp: true, ArrowDown: true, ArrowLeft: true, ArrowRight: true, PageUp: true, PageDown: true };

/** Words that narrower toolbars hide visually: `wide` below 1100px, `roomy` below 720px. They stay in the
 * button's text, so its accessible name is always complete. */
function Words({ hide, children }: { hide: "wide" | "roomy"; children: ComponentChildren }) {
  return <span class={hide}>{children}</span>;
}

function StatusPill() {
  const s = status.value;
  const n = pending.value.length;
  let cls = "ok";
  let Icon = CloudCheck;
  let text: ComponentChildren = <Words hide="roomy">Saved</Words>;
  if (s.kind === "error") {
    [cls, Icon, text] = ["error", TriangleAlert, <>Not saved<Words hide="wide">: tap for details</Words></>];
  } else if (s.kind === "offline") {
    // "Offline: 3 changes waiting", "3 waiting", then just "3".
    const changes = `change${n === 1 ? "" : "s"} `;
    text = <><Words hide="wide">Offline: </Words>{n}<Words hide="roomy">{" "}<Words hide="wide">{changes}</Words>waiting</Words></>;
    [cls, Icon] = ["offline", CloudOff];
  } else if (s.kind === "saving" || n) {
    [cls, Icon, text] = ["saving", CloudUpload, <Words hide="roomy">Saving…</Words>];
  } else if (proposals.value.length) {
    [cls, Icon, text] = ["error", TriangleAlert, <>{proposals.value.length}<Words hide="roomy"> to review</Words></>];
  } else if (show.value.lock) {
    [cls, Icon, text] = ["locked", Lock, <Words hide="roomy">Read-only</Words>];
  }
  return (
    <button type="button" class={`status-pill ${cls}`} data-status={cls} aria-label={cls === "ok" ? "Save status: Saved" : undefined} data-tooltip="Save status" data-tooltip-description="Review waiting changes, reconnect, and check whether this show is ready for offline use." onClick={() => (dialog.value = "status")}>
      <Icon />
      <span>{text}</span>
    </button>
  );
}

/** Depend on kinds, not snapshot objects: saving a cue must not restart the ready chip. */
function OfflineChip() {
  const ready = scriptLoad.value.stage === "ready";
  const startedAt = scriptLoad.value.startedAt;
  const kind = offline.value.kind;
  const [showPreparing, setShowPreparing] = useState(false);
  const [showReady, setShowReady] = useState(false);
  useEffect(() => {
    setShowPreparing(false);
    if (!ready) return;
    const timer = setTimeout(() => setShowPreparing(true), 2000);
    return () => clearTimeout(timer);
  }, [ready, startedAt]);
  useEffect(() => {
    setShowReady(ready && kind === "ready");
    if (!ready || kind !== "ready") return;
    const timer = setTimeout(() => setShowReady(false), 4000);
    return () => clearTimeout(timer);
  }, [ready, startedAt, kind]);
  const using = usingOffline.value;
  const text = !ready ? ""
    : using ? "Using offline copy"
    : kind === "unavailable" ? "Offline copy not ready"
    : kind === "preparing" && showPreparing ? "Offline copy: preparing…"
    : kind === "ready" && showReady ? "Offline copy ready" : "";
  if (!text) return null;
  return (
    <button type="button" class={`offline-chip ${using ? "using" : kind}`} aria-label={`${text}. Open Save status${kind === "unavailable" ? " to retry offline preparation" : ""}`} data-tooltip={text} data-tooltip-description={offline.value.message || "Open Save status for offline-copy details and preparation controls."} onClick={() => (dialog.value = "status")}>
      {using || kind === "unavailable" ? <CloudOff /> : kind === "ready" ? <CloudCheck /> : <CloudUpload />}
      <span>{text}</span>
    </button>
  );
}

function Pager() {
  const index = ix.value;
  const ready = scriptLoad.value.stage === "ready";
  const count = pageCount.value || index?.doc.project.source.pageCount || 0;
  const n = ready ? currentPage.value : Math.max(1, Math.min(view.value.page, count || view.value.page));
  const label = index?.page(n).label;
  const [typed, setTyped] = useState<string | null>(null);
  const go = (v: string) => {
    setTyped(null);
    const t = v.trim();
    if (!index || !t) return;
    // A printed page label wins; "#12" or a number with no such label means a PDF page.
    const byLabel = t.startsWith("#") ? undefined : index.doc.pages.find((p) => p.label === t);
    goToPage(byLabel ? byLabel.page : Number(t.replace("#", "")) || n);
  };
  return (
    <span class="pager tool-group">
      <button type="button" class="icon" aria-label="Previous page" data-tooltip="Previous page" data-tooltip-description={n <= 1 ? "You are on the first PDF page." : "Go to the previous PDF page, regardless of printed page numbers."} data-tooltip-keys="[" disabled={!ready || n <= 1} onClick={() => goToPage(n - 1)}>
        <ChevronLeft />
      </button>
      <input
        aria-label="Page"
        disabled={!ready}
        inputMode="numeric"
        value={typed ?? (label || (count ? String(n) : "—"))}
        onFocus={(e) => (e.target as HTMLInputElement).select()}
        onInput={(e) => setTyped((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => e.key === "Enter" && go((e.target as HTMLInputElement).value)}
        onBlur={(e) => typed !== null && go((e.target as HTMLInputElement).value)}
      />
      <span class="of">{!ready ? `PDF —/${count || "—"}` : label ? `PDF ${n}/${count || "—"}` : `/ ${count || "—"}`}</span>
      <button type="button" class="icon" aria-label="Next page" data-tooltip="Next page" data-tooltip-description={n >= count ? "You are on the last PDF page." : "Go to the next PDF page, regardless of printed page numbers."} data-tooltip-keys="]" disabled={!ready || !count || n >= count} onClick={() => goToPage(n + 1)}>
        <ChevronRight />
      </button>
    </span>
  );
}

function Banners() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, []);
  const oldest = pending.value[0]?.at;
  const out = [];
  if (environment.value !== "production") {
    out.push(<div class="banner warn"><strong>{environment.value.toUpperCase()}</strong> — Not the production installation. Use synthetic scripts only.</div>);
  }
  if (show.value.lock) {
    out.push(
      <div class="banner info" data-testid="lock-banner">
        {show.value.lock}{" "}
        {pending.value.length + proposals.value.length > 0 && (
          <button type="button" onClick={() => (dialog.value = "status")}>
            Unsaved changes
          </button>
        )}
      </div>,
    );
  }
  if (proposals.value.length) {
    out.push(
      <div class="banner warn" data-testid="proposals-banner">
        {proposals.value.length} change{proposals.value.length === 1 ? " wasn't" : "s weren't"} saved because someone else changed the same thing.{" "}
        <button type="button" onClick={() => (dialog.value = "proposals")}>
          Review
        </button>
      </div>,
    );
  }
  if (oldest && now - oldest > LONG_PENDING_MS) {
    out.push(
      <div class="banner warn">
        Some changes haven't reached q2cue for over 10 minutes. Check your internet connection.{" "}
        <button type="button" onClick={downloadPending}>
          Download unsaved changes
        </button>
      </div>,
    );
  }
  // Hidden departments or layers hide their marks here, so say so, with a way back.
  const index = ix.value;
  const hidden = index
    ? [
        ...index.sortedDepartments.filter((d) => view.value.hiddenDepartments.includes(d.id)).map((d) => d.code),
        ...index.sortedLayers.filter((l) => view.value.hiddenLayers.includes(l.id)).map((l) => l.name),
      ]
    : [];
  if (hidden.length) {
    out.push(
      <div class="banner info">
        Hidden on this device: {hidden.join(", ")}{" "}
        <button type="button" onClick={() => (view.value = { ...view.value, hiddenDepartments: [], hiddenLayers: [] })}>
          Show all
        </button>
      </div>,
    );
  }
  return out.length ? <div class="banners">{out}</div> : null;
}

function Notices() {
  return (
    <div class="notices" aria-live="polite">
      {notices.value.map((n) => (
        <div class={`notice ${n.tone}`}>
          <span>{n.text}</span>
          {n.action && (
            <button
              type="button"
              onClick={() => {
                n.action?.run();
                dismiss(n.id);
              }}
            >
              {n.action.label}
            </button>
          )}
          <button type="button" aria-label="Dismiss" onClick={() => dismiss(n.id)}>
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

/** Who else has this show open: initials, with page and what they're doing on hover. */
function Presence() {
  const others = presence.value.filter((p) => p.tab !== tabId);
  if (!others.length) return null;
  const names = [...new Map(others.map((p) => [p.userId + p.tab, p])).values()];
  return (
    <button type="button" class="presence" aria-label={`${names.length} other${names.length === 1 ? "" : "s"} here`} data-tooltip="Who's here" data-tooltip-description={names.map((p) => `${p.name} (${p.role}) · p. ${p.page}${p.editing ? ` · ${p.editing}` : ""}`).join("\n")} onClick={() => (dialog.value = "members")}>
      <Users />
      <span>{names.length}</span>
    </button>
  );
}

/** Pointer and keyboard tool choices must have identical draft/selection behavior. */
function chooseMouseTool(mode: "interact" | "place"): void {
  finishArrange();
  view.value = { ...view.value, mouseMode: mode };
}

export function App() {
  const title = ix.value?.doc.project.title ?? show.value.title ?? "q2cue";
  const env = environment.value;
  const ready = scriptLoad.value.stage === "ready";
  useEffect(() => {
    document.title = `${env !== "production" ? `${env.toUpperCase()} · ` : ""}${title} · q2cue`;
  }, [title, env]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      if ((dialog.value === "shortcuts" || dialog.value === "menu") && e.key === "Escape" && !e.altKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        dialog.value = null;
        document.getElementById("viewerContainer")?.focus({ preventScroll: true });
        return;
      }
      if (!dialog.value && cueListOpen.value && e.key === "Escape" && !e.altKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        cueListOpen.value = false;
        document.getElementById("viewerContainer")?.focus({ preventScroll: true });
        return;
      }
      const target = e.target instanceof Element ? e.target : null;
      const inPDF = Boolean(target?.closest("#viewerContainer"));
      const key = e.key.toLowerCase();
      const showNavigation = inPDF && (key === "c" || key === "m") && !e.altKey && !e.ctrlKey && !e.metaKey;
      if (dialog.value || (cueListOpen.value && !showNavigation)) {
        // A focused scroll container also has native keyboard scrolling. Do not let it
        // move the book behind an overlay after declining the app's own shortcut.
        if (inPDF && !e.altKey && !e.ctrlKey && !e.metaKey && Object.hasOwn(PDF_SCROLL_KEYS, e.key)) e.preventDefault();
        return;
      }
      // Fields and composite widgets own text, Undo and Escape as well as navigation.
      if (target?.closest(
        "input, textarea, select, [contenteditable]:not([contenteditable='false']), [role='slider'], [role='spinbutton'], [role='combobox'], [role='listbox'], [role='menu'], [role='tablist'], [role='textbox']",
      )) return;
      if (e.key === "Escape" && arranging.value) {
        e.preventDefault();
        finishArrange();
        return;
      }
      if (scriptLoad.value.stage !== "ready") return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      // Keep existing Undo/Escape usable after toolbar clicks; new PDF keys must
      // not steal arrow/typeahead behavior or activate a focused control.
      if (target?.closest("button, a[href], summary, [role='separator'], [role='button'], [role='switch'], [role='checkbox'], [role='radio']")) return;
      if (!e.shiftKey && Object.hasOwn(PDF_SCROLL_KEYS, e.key)) {
        e.preventDefault();
        scrollViewer(e.key);
        return;
      }
      // Character shortcuts are focus-only, so dictation or typing elsewhere cannot change tools.
      if (!inPDF) return;
      if (key === "[" || key === "]") {
        e.preventDefault();
        goToPage(currentPage.value + (key === "]" ? 1 : -1));
      } else if (key === "-" || key === "+" || key === "=") {
        e.preventDefault();
        zoomBy(key === "-" ? -1 : 1);
      } else if (["i", "p", "s", "v", "f", "c", "m", "?"].includes(key)) {
        e.preventDefault();
        if (e.repeat) return;
        switch (key) {
          case "i": chooseMouseTool("interact"); break;
          case "p": chooseMouseTool("place"); break;
          case "s": if (!arranging.value) startArrange(); break;
          case "v": view.value = { ...view.value, preview: !view.value.preview }; break;
          case "f": fit("page-width"); break;
          case "c":
            cueListView.value = "cues";
            cueListOpen.value = true;
            break;
          case "m":
            dialog.value = "menu";
            requestAnimationFrame(() => {
              if (dialog.value === "menu") document.querySelector<HTMLDivElement>('.sheet[aria-label="Menu"] .sheet-body')?.focus({ preventScroll: true });
            });
            break;
          case "?": dialog.value = "shortcuts"; break;
        }
      }
    };
    addEventListener("keydown", onKey);
    addEventListener("beforeunload", (e) => {
      if (pending.value.some((p) => !p.held)) e.preventDefault();
    });
    return () => removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <header class="toolbar">
        <span class="tool-group">
          <a class="icon button" href="/" aria-label="All shows" data-tooltip="All shows" data-tooltip-description="Return to the shows you can open. Waiting changes and drafts stay on this device.">
            <House />
          </a>
        </span>
        <span class="tool-group" role="group" aria-label="History">
          <button type="button" class="icon" aria-label="Undo" disabled={!ready || !undoStack.value.length} onClick={undo} data-tooltip="Undo" data-tooltip-description={undoStack.value.length ? `Undo ${undoStack.value.at(-1)?.label}.` : "No changes to undo in this tab."} data-tooltip-keys="Ctrl/⌘|Z">
            <Undo2 />
          </button>
          <button type="button" class="icon" aria-label="Redo" disabled={!ready || !redoStack.value.length} onClick={redo} data-tooltip="Redo" data-tooltip-description={redoStack.value.length ? `Redo ${redoStack.value.at(-1)?.label}.` : "No changes to redo in this tab."} data-tooltip-keys="Ctrl/⌘|Shift|Z">
            <Redo2 />
          </button>
        </span>
        <span class="tool-group" role="group" aria-label="Mouse tool">
          {(["place", "interact"] as const).map((mode) => (
            <button
              type="button"
              class="icon"
              aria-label={mode === "place" ? "Place" : "Interact"}
              aria-pressed={!arranging.value && view.value.mouseMode === mode}
              disabled={!ready}
              onClick={() => chooseMouseTool(mode)}
              data-tooltip={mode === "place" ? "Place" : "Interact"}
              data-tooltip-description={mode === "place" ? "Place, move and resize marks with the mouse. Pencil input is unchanged." : "Click existing marks to edit. The mouse cannot place or move marks."}
              data-tooltip-keys={mode === "place" ? "P" : "I"}
            >
              {mode === "place" ? <Pencil /> : <MousePointer2 />}
            </button>
          ))}
          <button
            type="button"
            class="icon"
            aria-label="Select cues"
            data-tooltip="Select cues"
            data-tooltip-description={!ready ? "Wait for the current PDF page to finish loading." : !canEdit.value ? "This show is read-only; cue positions cannot be arranged." : panel.value ? "Save or cancel the current draft before selecting cues." : view.value.preview ? "Exit Preview before selecting cues." : "Select cue moments on one page to align or distribute their GO positions."}
            data-tooltip-keys="S"
            aria-pressed={arranging.value}
            disabled={!ready || !canEdit.value || Boolean(panel.value) || view.value.preview}
            onClick={() => arranging.value ? finishArrange() : startArrange()}
          >
            <SquareDashedMousePointer />
          </button>
        </span>
        <span class="spacer" />
        <Pager />
        <span class="spacer" />
        <span class="tool-group" role="group" aria-label="Zoom">
          <button type="button" class="icon zoom-step" aria-label="Zoom out" data-tooltip="Zoom out" data-tooltip-description="Make the PDF smaller without changing its annotations." data-tooltip-keys="−" disabled={!ready} onClick={() => zoomBy(-1)}>
            <Minus />
          </button>
          <button type="button" class="zoom" aria-label="Fit width" disabled={!ready} onClick={() => fit("page-width")} data-tooltip="Fit width" data-tooltip-description={`Fit the PDF to its available width. Current zoom: ${Math.round(zoom.value * 100)}%.`} data-tooltip-keys="F">
            {ready ? `${Math.round(zoom.value * 100)}%` : "—"}
          </button>
          <button type="button" class="icon zoom-step" aria-label="Zoom in" data-tooltip="Zoom in" data-tooltip-description="Make the PDF larger. Arrow keys pan across a zoomed page." data-tooltip-keys="+" disabled={!ready} onClick={() => zoomBy(1)}>
            <Plus />
          </button>
        </span>
        <span class="tool-group" role="group" aria-label="Views">
          <button
            type="button"
            class="labelled"
            aria-label="Preview"
            aria-pressed={view.value.preview}
            disabled={!ready}
            data-tooltip="Preview"
            data-tooltip-description="Show pages as the exported prompt book lays them out, without editing controls."
            data-tooltip-keys="V"
            onClick={() => (view.value = { ...view.value, preview: !view.value.preview })}
          >
            <Eye />
            <Words hide="wide">Preview</Words>
          </button>
          <button type="button" class="labelled" aria-label="Cues" data-tooltip="Cues" data-tooltip-description="Open the cue and note lists. Press C to show the cue list." data-tooltip-keys="C" aria-pressed={cueListOpen.value} onClick={() => (cueListOpen.value = !cueListOpen.value)}>
            <ListOrdered />
            <Words hide="wide">Cues</Words>
          </button>
        </span>
        <Presence />
        <StatusPill />
        <OfflineChip />
        <span class="tool-group">
          <button type="button" class="icon" aria-label="Menu" data-tooltip="Menu" data-tooltip-description="Manage this show, people, exports, saving and keyboard-shortcut help." data-tooltip-keys="M" onClick={() => (dialog.value = "menu")}>
            <Ellipsis />
          </button>
        </span>
      </header>
      <Banners />
      <ScriptLoading />
      <div class="pdf-editor-host" hidden={!ready} inert={!ready}>
        {panel.value && <Panel />}
        {arranging.value && <Arrange />}
        {!panel.value && !arranging.value && !view.value.preview && <CueNavigation />}
      </div>
      <CueList readOnly={!ready} />
      <Dialogs />
      <Notices />
      <ButtonTooltips />
    </>
  );
}
